import { FC, useEffect, useRef, useState } from 'react';
import { QueueButton } from 'packages/task-man/components/sample-group-task-queues/queue-button';
import { useTaskQueue } from 'packages/task-man/service/pickup';
import { AnnotationTool, Role } from 'packages/task-man/constants';
import { TaskGroupType } from 'packages/lib/api/annotations/contract/enums';
import { workflowToUrlTemplate } from 'packages/task-man/components/sample-group-task-queues';
import { Skeleton } from 'antd';
import { EmptyTaskQueueDialog } from 'packages/task-man/components/dialogs/empty-queue-dialog';
import { TaskNotAvailableDialog } from 'packages/task-man/components/dialogs/task-unavailable-dialog';
import { TaxonomyInstruction } from 'packages/lib/api/annotations/contract/taxonomy';
import { CategoriesResp } from 'packages/lib/api/annotations/contract/category';
import { Attribute } from 'packages/lib/api/annotations/contract/attribute';
import { getTenantBasepath } from 'packages/lib/auth/is-path';
import {
  labelingRoleToPermissionRole,
  userHasAnyPermission,
} from 'packages/lib/auth/permissions';

interface QueueCardProps {
  workflow: AnnotationTool;
  sampleGroupToken: string;
  labelingRole: string;
  taskGroupType: TaskGroupType;
  name: string;
  user: string;
  allClassificationData?: {
    allClassifications?: CategoriesResp;
    allAttributes?: Attribute[];
    allTaxonomyInstructions?: TaxonomyInstruction[];
  };
  toDoTaskCount?: number;
  setTaskGroupCount: (taskGroupToken: string, count: number) => void;
}

export const QueueCard: FC<QueueCardProps> = (props) => {
  const {
    workflow,
    sampleGroupToken,
    labelingRole,
    taskGroupType,
    name,
    user,
    allClassificationData,
    toDoTaskCount,
    setTaskGroupCount,
  } = props;
  const { data: taskQueue, isLoading } = useTaskQueue({
    role: labelingRole as Role,
    sampleGroupToken,
    taskGroupType,
    userEmail: user,
    ...allClassificationData,
  });
  const [loading, setLoading] = useState(false);
  const [taskCount, setTaskCount] = useState<number | undefined>(toDoTaskCount);
  const [hasAssignedTask, setHasAssignedTask] = useState(false);
  const [emptyQueueDialogOpen, setEmptyQueueDialogOpen] = useState(false);
  const [noTaskDialogOpen, setNoTaskDialogOpen] = useState(false);
  const didSetTaskCounts = useRef(false);

  const validPermissions = labelingRoleToPermissionRole[labelingRole].map(
    (role) => `${workflow}:${role}`
  );
  const hasPermission = userHasAnyPermission(
    taskQueue?.user ?? { user_id: '', permissions: '' },
    validPermissions
  );

  useEffect(() => {
    if (didSetTaskCounts.current) {
      return;
    }

    if (taskQueue) {
      didSetTaskCounts.current = true;
      void Promise.all([
        taskQueue.count('assigned'),
        taskQueue.count('unassigned'),
      ]).then(([assignedTaskCount, unassignedTaskCount]) => {
        const taskCount = assignedTaskCount + unassignedTaskCount;
        setTaskCount(taskCount);
        setTaskGroupCount(taskQueue.taskGroup.token, taskCount);
        setHasAssignedTask(!!assignedTaskCount);
      });
    }
  }, [setTaskGroupCount, taskCount, taskQueue]);

  const handleAssignTask = async () => {
    if (!taskQueue) return;
    setLoading(true);
    const [task] = await taskQueue.peekUnassigned(1);
    // If there are no tasks available in this queue, show a dialog message and make sure the queue card disappears.
    if (!task) {
      setLoading(false);
      setEmptyQueueDialogOpen(true);
      return;
    }

    const taskUrl = `${getTenantBasepath()}${workflowToUrlTemplate[workflow](
      task,
      taskQueue.sampleGroup.token
    )}`;

    // Multi-task workflows (Classification/Scene enrichment) handle their own task assignment
    if (
      workflow === 'classification-enrichment' ||
      workflow === 'scene-enrichment'
    ) {
      window.open(taskUrl, '_blank');
      setLoading(false);
      return;
    }

    await taskQueue.assignTasks([task]);
    window.open(taskUrl, '_blank');
    const [assignedTaskCount, unassignedTaskCount] = await Promise.all([
      taskQueue.count('assigned'),
      taskQueue.count('unassigned'),
    ]);
    setTaskCount(assignedTaskCount + unassignedTaskCount);
    setLoading(false);
    setHasAssignedTask(true);
    return;
  };

  const handleContinueTask = async () => {
    if (!taskQueue || !hasPermission) return;
    setLoading(true);
    const [task] = await taskQueue.getManyAssignedTasks(1);
    // If the assigned task is no longer available, show a dialog message and make sure the user has the option to pickup a new task.
    if (!task) {
      setLoading(false);
      setNoTaskDialogOpen(true);
      setHasAssignedTask(false);
      setTaskCount(await taskQueue.count('unassigned'));
      return;
    }
    const taskUrl = `${getTenantBasepath()}${workflowToUrlTemplate[workflow](
      task,
      taskQueue.sampleGroup.token
    )}`;
    window.open(taskUrl, '_blank');
    setLoading(false);
    return;
  };

  if (isLoading) {
    return <Skeleton.Button active style={{ height: 256, width: 320 }} />;
  }
  if (!taskQueue || !taskCount || taskCount === 0) {
    return null;
  }

  const closeEmptyQueueDialog = () => {
    setEmptyQueueDialogOpen(false);
    setTaskCount(0);
  };

  const closeNoTaskDialog = () => setNoTaskDialogOpen(false);

  return (
    <div>
      <QueueButton
        onClick={hasAssignedTask ? handleContinueTask : handleAssignTask}
        type={hasAssignedTask ? 'primary' : 'default'}
        text={hasAssignedTask ? 'Continue' : 'Begin New Task'}
        disabled={!taskQueue || !taskQueue.user || !hasPermission}
        queueName={name}
        sampleGroupToken={sampleGroupToken}
        nTasks={taskCount}
        loading={loading}
      />
      <EmptyTaskQueueDialog
        open={emptyQueueDialogOpen}
        queueName={name}
        onClose={closeEmptyQueueDialog}
      />
      <TaskNotAvailableDialog
        open={noTaskDialogOpen}
        onPickup={handleAssignTask}
        onClose={closeNoTaskDialog}
      />
    </div>
  );
};
